import { useCallback } from 'react';

import { cn, StatusChip } from '@src/components/ui';
import { INVITE_ROLES, ROLE_CHIP, ROLE_RANK } from '@src/constants';
import { useOptionLabels } from '@src/query';


/** Returns a stable `(role) => label` resolver backed by the metadata-options
 * hook (#364). MUST be called at component top level; the returned function is
 * safe to thread through props and call in event handlers / titles. */
function useRoleLabel() {
  const labels = useOptionLabels('membership_role');
  return useCallback((role) => labels[role] || role || '', [labels]);
}


function isDowngrade(fromRole, toRole) {
  return (ROLE_RANK[toRole] ?? 0) < (ROLE_RANK[fromRole] ?? 0);
}


function initials(user) {
  const letters = [user?.first_name, user?.last_name]
    .map((part) => (part || '').charAt(0))
    .join('');
  return (letters || (user?.email || '?').charAt(0)).toUpperCase();
}


function fullName(user) {
  return `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
}


/** "Jun 3, 2026" — short, locale-stable display for joined/sent dates. */
function formatDate(value) {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}


/** Routes through the StatusChip primitive (#367) so role chips share one chip
 * implementation. `bordered={false}` + the ROLE_CHIP class override (merged last
 * via cn, so it wins) keep the bespoke /15 soft-tint and tight metrics
 * pixel-identical — no visible appearance change. */
function RoleChip({ role, className }) {
  const roleLabel = useRoleLabel();
  return (
    <StatusChip
      className={cn(
        'px-2.5 py-[3px] text-[11px]',
        ROLE_CHIP[role] || ROLE_CHIP.viewer,
        className,
      )}
      bordered={false}
    >
      {roleLabel(role)}
    </StatusChip>
  );
}

RoleChip.displayName = 'RoleChip';


/** Gradient initials avatar (members) or neutral fallback (pending invites). */
function Avatar({ user, size = 34, muted = false }) {
  const style = { width: size, height: size, fontSize: Math.round(size * 0.36) };
  if (muted) {
    return (
      <span
        style={style}
        className="grid shrink-0 place-items-center rounded-full border border-hairline-strong bg-surface-2"
      />
    );
  }
  return (
    <span
      style={style}
      className="bg-brand-gradient grid shrink-0 place-items-center rounded-full font-bold text-white"
    >
      {initials(user)}
    </span>
  );
}

Avatar.displayName = 'Avatar';

export {
  Avatar,
  formatDate,
  fullName,
  initials,
  INVITE_ROLES,
  isDowngrade,
  RoleChip,
  useRoleLabel,
};
